import React from 'react';
import { ContentBlock, TermsAndCondition } from 'shared/types/product/Product';
import CloseIcon from 'public/icons/ag-icons/Close.svg';
import { useTablet } from 'helpers/hooks/useTabletDevice';
import useMobileDevice from 'helpers/hooks/useMobileDevice';

interface TermsContentProps {
  termsAndCondition: TermsAndCondition;
  setOpen: (value: boolean) => void;
  modalRef: React.RefObject<HTMLDivElement>;
}

const TermsContent: React.FC<TermsContentProps> = ({ termsAndCondition, setOpen, modalRef }) => {
  const { isMobileDevice } = useMobileDevice();
  const isTablet = useTablet();
  const modalWidth =
    (isMobileDevice && termsAndCondition?.modalWidth?.mobile) ||
    (isTablet && termsAndCondition?.modalWidth?.tablet) ||
    termsAndCondition?.modalWidth?.desktop ||
    '560px';
  const modalHeight =
    (isMobileDevice && termsAndCondition?.modalHeight?.mobile) ||
    (isTablet && termsAndCondition?.modalHeight?.tablet) ||
    termsAndCondition?.modalHeight?.desktop ||
    '80vh';
  const headingStyle = termsAndCondition?.headingStyle || '';
  const contentBlocks = termsAndCondition?.contentBlocks;
  const className = 'font-body leading-[100%]';
  return (
    <>
      <div
        className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-50"
        data-qa="terms-and-conditions-modal"
      >
        <div
          className="relative flex flex-col items-start gap-16 overflow-y-auto rounded-md bg-white px-16  py-24 shadow-lg md:px-24"
          style={{ width: modalWidth, maxHeight: modalHeight }}
          ref={modalRef}
          data-qa="terms-and-condition-modal"
        >
          <div
            className="absolute right-0 top-0 flex h-40 w-40 items-center justify-center gap-10"
            onClick={() => setOpen(false)}
            data-qa="terms-and-condition-close-icon"
          >
            <div className="flex h-24 w-24 shrink-0 items-center justify-center">
              <CloseIcon strokeWidth={1} className="cursor-pointer text-[#1E1E1E]" />
            </div>
          </div>
          {/* Header */}
          <div className="flex items-center justify-between" data-qa="terms-condition-heading">
            <p
              style={{
                fontSize: isMobileDevice
                  ? `${headingStyle?.fontSizeMobilePx ?? 18}px`
                  : `${headingStyle?.fontSizeDesktopPx ?? 18}px`,
                fontWeight: headingStyle?.fontWeight ?? 'bold',
                color: headingStyle?.textColor ?? '#1E1E1E',
              }}
              className="font-body leading-[100%]"
              data-qa="terms-condition-heading-content"
            >
              {termsAndCondition?.heading || 'Terms and Conditions'}
            </p>
          </div>

          {contentBlocks?.map((block: ContentBlock, index: number) => {
            // Fallbacks for styles
            const headingStyle = block?.headingStyle;
            const paragraphStyle = block?.paragraphStyle;
            const listStyle = block?.listStyle;
            // Heading
            if (block.type === 'heading') {
              const style = {
                fontSize: isMobileDevice
                  ? `${headingStyle?.fontSizeMobilePx ?? 18}px`
                  : `${headingStyle?.fontSizeDesktopPx ?? 18}px`,
                fontWeight: headingStyle?.fontWeight ?? 600,
                color: headingStyle?.textColor ?? '#1E1E1E',
              };
              return block.useRichText ? (
                <p
                  key={index}
                  style={style}
                  className={className}
                  dangerouslySetInnerHTML={{ __html: block?.text || '' }}
                  data-qa="block-heading-content"
                />
              ) : (
                <p key={index} style={style} className={className} data-qa="block-heading-content">
                  {block?.text}
                </p>
              );
            }

            // Paragraph
            if (block.type === 'paragraph') {
              const style = {
                fontSize: isMobileDevice
                  ? `${paragraphStyle?.fontSizeMobilePx ?? 14}px`
                  : `${paragraphStyle?.fontSizeDesktopPx ?? 16}px`,
                fontWeight: paragraphStyle?.fontWeight ?? 400,
                color: paragraphStyle?.textColor ?? '#1E1E1E',
                marginTop: '0.5rem',
              };
              return block.useRichText ? (
                <p
                  key={index}
                  style={style}
                  className={className}
                  dangerouslySetInnerHTML={{ __html: block?.text || '' }}
                  data-qa="block-paragraph-content"
                />
              ) : (
                <p key={index} style={style} className={className} data-qa="block-paragraph-content">
                  {block?.text}
                </p>
              );
            }

            // List
            if (block?.type === 'list' && block?.items) {
              return (
                <ul key={index} className="self-stretch" data-qa="block-list">
                  {block?.items?.map((item, idx) => {
                    const liStyle = {
                      fontSize: isMobileDevice
                        ? `${listStyle?.fontSizeMobilePx ?? 12}px`
                        : `${listStyle?.fontSizeDesktopPx ?? 14}px`,
                      fontWeight: listStyle?.fontWeight ?? 400,
                      color: listStyle?.textColor ?? '#1E1E1E',
                      marginBottom: '1rem',
                    };
                    return item.useRichText ? (
                      <li
                        key={idx}
                        style={liStyle}
                        className={`font-body leading-[130%]`}
                        dangerouslySetInnerHTML={{ __html: item?.text || '' }}
                        data-qa={`block-list-content-${idx}`}
                      />
                    ) : (
                      <li
                        key={idx}
                        style={liStyle}
                        className={`font-body leading-[130%]`}
                        data-qa={`block-list-content-${idx}`}
                      >
                        {item?.text}
                      </li>
                    );
                  })}
                </ul>
              );
            }

            return null;
          })}
        </div>
      </div>
    </>
  );
};

export default TermsContent;
